import type { RGB } from '../types/RGB';
import type { Result, AppError } from '../../error/types';
import { ok, err } from '../../error/functions';
import { REGEX_PATTERNS } from '../../regex';

/**
 * @group Functions
 * @summary <blockquote>Converts hexadecimal color string to RGB color values.</blockquote>
 * @param hex - Hexadecimal color string
 * @returns Result containing RGB color object or an error if invalid hex
 * @example
 * ```typescript
 * const result = hexToRgb("#ff8000");
 * result.match({
 *   ok: (rgb) => {
 *     // Returns: { r: 255, g: 128, b: 0 }
 *   },
 *   err: (error) => {
 *     // Handle invalid hex format
 *   }
 * });
 * ```
 */
export const /**
 * @internal
 */
hexToRgb = (hex: string): Result<RGB, AppError> => {
  const result = REGEX_PATTERNS.color.HEX_COLOR.regex.exec(hex)
  return result
    ? ok({
        r: Number.parseInt(result[1], 16),
        g: Number.parseInt(result[2], 16),
        b: Number.parseInt(result[3], 16),
      })
    : err({
        errorCode: 'INVALID_HEX_FORMAT',
        errorMessage: `Invalid hex color format: ${hex}. Expected format: #RRGGBB or RRGGBB`
      })
} 